import type { UiChartNode } from "@agent/uiSchema";

const unitAliases = new Map(
  Object.entries({
    kilogram: "kg",
    kilograms: "kg",
    gram: "g",
    grams: "g",
    pound: "lb",
    pounds: "lb",
    lbs: "lb",
    minute: "min",
    minutes: "min",
    mins: "min",
    second: "s",
    seconds: "s",
    sec: "s",
    secs: "s",
    hour: "h",
    hours: "h",
    hr: "h",
    hrs: "h",
    "beats/minute": "bpm",
    "beats/min": "bpm",
  }),
);

const comparableUnit = (unit: string) =>
  unitAliases.get(unit.trim()) ?? unit.trim();

export const chartLabelWithoutUnit = (label: string, unit?: string | null) => {
  let result = label.trim();
  if (!unit?.trim()) return result;
  let suffix = result.match(/\s*\(([^()]*)\)$/);
  while (suffix && comparableUnit(suffix[1]) === comparableUnit(unit)) {
    result = result.slice(0, suffix.index).trimEnd();
    suffix = result.match(/\s*\(([^()]*)\)$/);
  }
  return result || "Value";
};

export const chartLabelWithUnit = (label: string, unit?: string | null) =>
  `${chartLabelWithoutUnit(label, unit)}${unit?.trim() ? ` (${unit.trim()})` : ""}`;

export const chartNumber = (value: number) =>
  new Intl.NumberFormat(undefined, { maximumSignificantDigits: 21 }).format(
    value,
  );

export const chartColor = (index: number) =>
  `var(--color-chart-${(index % 6) + 1})`;

/** Palette repeats every 6; pie slices past that are hatched to stay distinct. */
export const chartHatched = (node: UiChartNode, index: number) =>
  node.chartType === "pie" && index >= 6;

export const chartSwatch = (node: UiChartNode, index: number) =>
  chartHatched(node, index)
    ? `repeating-linear-gradient(45deg, ${chartColor(index)} 0 2px, var(--color-card) 2px 3.5px)`
    : chartColor(index);

export const horizontalBars = (node: UiChartNode) =>
  node.chartType === "bar" &&
  node.data.length <= 12 &&
  node.data.some((row) => String(row[node.xKey]).length > 18);

export const chartHeight = (node: UiChartNode) =>
  horizontalBars(node) ? Math.max(280, node.data.length * 64 + 24) : 280;

export const chartX = (node: UiChartNode, value: string | number) => {
  if (node.xType === "date") {
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    }).format(
      new Date(typeof value === "number" ? value : `${value}T00:00:00Z`),
    );
  }
  return typeof value === "number" ? chartNumber(value) : value;
};

export const chartValue = (node: UiChartNode, value: unknown) =>
  typeof value === "number"
    ? `${chartNumber(value)}${node.unit ? ` ${node.unit}` : ""}`
    : "No measurement";

export const chartHasValues = (node: UiChartNode) =>
  node.data.some((row) =>
    node.series.some(({ key }) =>
      node.chartType === "pie"
        ? typeof row[key] === "number" && row[key] > 0
        : typeof row[key] === "number",
    ),
  );
